import React, {
  ComponentProps,
  FunctionComponent,
  memo,
  useCallback
} from 'react';
import { animated, useSpring } from '@react-spring/web';
import useMeasure from 'react-use-measure';
import { StrictReactNode } from '../../types/StrictReactNode';
import { classes } from '../../utils/utils';
import { Icon } from '../../misc/Icon/Icon';
import { prepareProps } from '../../utils/propUtils';
import styles from './Accordion.module.css';

export type AccordionProps = ComponentProps<'div'> & {
  /**
   * Controls whether the accordion is opened or closed.
   */
  open: boolean;
  /**
   * Called when the user opens or closes the accordion.
   * The provided parameter `open` is what the prop `open` should be set to.
   */
  onToggle: (open: boolean) => void;
  /**
   * If set to `true`, prevents the user from opening or closing the accordion.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * The content displayed above the children.
   */
  summary: StrictReactNode;
  /**
   * Content displayed if the accordion is open.
   */
  children?: StrictReactNode;
};

export const Accordion: FunctionComponent<AccordionProps> = memo((props) => {
  const {
    open,
    onToggle,
    disabled = false,
    summary,
    children,
    ...remainingProps
  } = prepareProps({ props });

  const [ref, { height: contentHeight }] = useMeasure();
  const [animatedHeight] = useSpring(
    () => ({
      height: open ? contentHeight : 0
    }),
    [open, contentHeight]
  );

  const onDetailsClick = useCallback(() => {
    if (!disabled) {
      onToggle(!open);
    }
  }, [disabled, onToggle, open]);

  return (
    <div {...remainingProps}>
      <div
        className={classes(styles.summary, disabled && styles.disabled)}
        onClick={onDetailsClick}
      >
        <div data-testid="Accordion.Summary">{summary}</div>
        <div className={styles.summaryLine}></div>
        <Icon
          className={styles.summaryIcon}
          name={open ? 'chevron-up' : 'chevron-down'}
        />
      </div>
      <animated.div
        style={animatedHeight}
        className={styles.content}
      >
        <div
          ref={ref}
          data-testid="Accordion.Content"
          aria-hidden={!open}
        >
          {children}
        </div>
      </animated.div>
    </div>
  );
});
